import { type ReactNode, useEffect, useState } from 'react';

import { GenericTable } from '@backrs_ui/common-ui';

import { Box, type SxProps, TablePagination, type Theme } from '@mui/material';

/** Matches the users list this shell was taken from. */
const ROWS_PER_PAGE_OPTIONS = [50, 75, 100];
const DEFAULT_ROWS_PER_PAGE = 50;

export type PagedRows<T> = Readonly<{
  visible: readonly T[];
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  /** Absent where the page size is fixed; the table then renders no control to change it. */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}>;

/**
 * The page index outlives the list it indexes into: a narrowing shortens the rows under it, so
 * without the clamp the slice starts past the end and the table renders empty while the
 * pagination control still offers a page that is gone.
 */
export const usePagedRows = <T,>(rows: readonly T[]): PagedRows<T> => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const safePage = Math.min(page, lastPage);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [page, safePage]);

  const start = safePage * rowsPerPage;

  return {
    visible: rows.slice(start, start + rowsPerPage),
    count: rows.length,
    page: safePage,
    rowsPerPage,
    onPageChange: setPage,
    onRowsPerPageChange: (next: number) => {
      setRowsPerPage(next);
      setPage(0);
    },
  };
};

type RostersAdminTableProps = Readonly<{
  paginationId: string;
  headers: readonly ReactNode[];
  ariaLabel: string;
  paging: PagedRows<unknown>;
  children: ReactNode;
}>;

export const RostersAdminTable = ({
  paginationId,
  headers,
  ariaLabel,
  paging,
  children,
}: RostersAdminTableProps) => (
  <>
    <Box sx={scrollSx}>
      <GenericTable headers={headers} ariaLabel={ariaLabel} stickyHeader>
        {children}
      </GenericTable>
    </Box>
    <TablePagination
      id={paginationId}
      component="div"
      count={paging.count}
      page={paging.page}
      onPageChange={(_event, next) => paging.onPageChange(next)}
      rowsPerPage={paging.rowsPerPage}
      onRowsPerPageChange={(event) =>
        paging.onRowsPerPageChange?.(parseInt(event.target.value, 10))
      }
      rowsPerPageOptions={paging.onRowsPerPageChange === undefined ? [] : ROWS_PER_PAGE_OPTIONS}
      sx={paginationSx}
    />
  </>
);

// The sticky header needs the table's own container to be the scrollport; bounding this wrapper
// alone leaves it unbounded, since a TableContainer scrolls on its own.
const scrollSx: SxProps<Theme> = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  '& .MuiTableContainer-root': {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
};

const paginationSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  '& .MuiTablePagination-toolbar': {
    paddingLeft: backrs.spacing['spacing-3xl'],
    paddingRight: backrs.spacing['spacing-xl'],
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});
